/** Whole days from today to a YYYY-MM-DD date (negative = past). */
function daysUntil(dueDate: string): number {
  const [year, month, day] = dueDate.split('-').map(Number);
  const due = new Date(year!, month! - 1, day!);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((due.getTime() - today.getTime()) / 86_400_000);
}

/**
 * Returns a human-readable relative label for a YYYY-MM-DD due date string.
 * Overdue → "X days ago"   |  Today / Tomorrow  |  Future → "In X days"
 */
export function relativeDueDate(dueDate: string): string {
  const diffDays = daysUntil(dueDate);
  if (diffDays < -1) return `${-diffDays} days ago`;
  if (diffDays === -1) return '1 day ago';
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  return `In ${diffDays} days`;
}

/**
 * Like relativeDueDate, but phrases overdue dates as lateness ("2 days late")
 * and flags them so callers can tint the label.
 */
export function dueLabel(dueDate: string): { text: string; late: boolean } {
  const diffDays = daysUntil(dueDate);
  if (diffDays < 0) {
    return { text: diffDays === -1 ? '1 day late' : `${-diffDays} days late`, late: true };
  }
  return { text: relativeDueDate(dueDate), late: false };
}

/** Local date → YYYY-MM-DD. */
function toYmd(date: Date): string {
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${m}-${d}`;
}

/** YYYY-MM-DD → local Date (midnight). */
function fromYmd(ymd: string): Date {
  const [year, month, day] = ymd.split('-').map(Number);
  return new Date(year!, month! - 1, day!);
}

/** The Monday of the week containing the given date, as YYYY-MM-DD. */
export function mondayOf(date: Date): string {
  const monday = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  return toYmd(monday);
}

/** Add whole days to a YYYY-MM-DD string. */
export function addDays(ymd: string, days: number): string {
  const date = fromYmd(ymd);
  date.setDate(date.getDate() + days);
  return toYmd(date);
}

/**
 * Friendly label for a Mon–Sun week: "This week" / "Last week" / "Next week"
 * for the weeks around today, otherwise the date range ("12–18 May",
 * "28 Apr – 4 May", with years when the range leaves the current year).
 */
export function weekLabel(monday: string, locale?: string): string {
  const thisMonday = mondayOf(new Date());
  if (monday === thisMonday) return 'This week';
  if (monday === addDays(thisMonday, -7)) return 'Last week';
  if (monday === addDays(thisMonday, 7)) return 'Next week';
  return weekRangeLabel(monday, locale);
}

/**
 * The Mon–Sun date range of a week, e.g. "12–18 May" or "28 Apr – 4 May"
 * (day/month order and same-month collapsing follow the locale).
 */
export function weekRangeLabel(monday: string, locale?: string): string {
  const start = fromYmd(monday);
  const end = fromYmd(addDays(monday, 6));
  const currentYear = new Date().getFullYear();
  const withYear = start.getFullYear() !== currentYear || end.getFullYear() !== currentYear;
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
  }).formatRange(start, end);
}
